import { useMemo } from 'react';
import { ComboboxConfig } from './ComboboxConfig';
import { useConfigLookup } from '@/hooks/useConfigLookup';
import type { FKOption } from '@/hooks/useFKLookup';
import { trpcClient } from '@/lib/trpc';
import { useQueryClient } from '@tanstack/react-query';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { toast } from 'sonner';

interface ComboboxConfigMasterProps {
    categoryCode: string;
    value: string;
    onChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
    error?: boolean;
    disabled?: boolean;
    className?: string;
    hideLabel?: boolean;
    canAdd?: boolean; // Override if needed
    valueField?: 'code' | 'name'; // Lưu giá trị theo code hoặc name (chuỗi hiển thị)
    filterOptions?: (option: FKOption) => boolean;
}

/** Danh mục cố định theo enum DB (kể cả tên gọi khác) — chỉ chọn, không thêm mục mới */
const ENUM_BACKED_CATEGORIES = new Set([
    'deployment_model', 'priority', 'mold_type', 'mold_types', 'tooling_status', 'machine_type', 'machine_types',
]);

export function ComboboxConfigMaster({
    categoryCode,
    value,
    onChange,
    label,
    placeholder,
    required,
    error,
    disabled,
    className,
    hideLabel,
    canAdd,
    valueField = 'code',
    filterOptions,
}: ComboboxConfigMasterProps) {
    const { options, isLoading } = useConfigLookup(categoryCode, { valueField });
    const filteredOptions = useMemo(
        () => (filterOptions ? options.filter(filterOptions) : options),
        [options, filterOptions],
    );
    const queryClient = useQueryClient();
    const { hasPermission, role } = useRoleAccess();
    
    // Dynamic Contextual RBAC Resolver
    const isAdmin = role === 'ADMIN' || hasPermission('*', '*');
    const hasCreatePerm = useMemo(() => {
        // Danh mục gắn enum DB: không thêm nhanh (máy chủ cũng chặn) — giá trị ngoài enum làm form lưu lỗi
        if (ENUM_BACKED_CATEGORIES.has(categoryCode.toLowerCase())) return false;
        if (canAdd !== undefined) return canAdd;
        if (isAdmin || hasPermission('configs', 'create')) return true;

        const code = categoryCode.toLowerCase();
        switch (code) {
            case 'customer_groups':
            case 'customer_group':
            case 'customer_position':
            case 'customer_positions':
            case 'color_tolerance':
            case 'delivery_methods':
            case 'shipping_method':
            case 'payment_terms':
            case 'deposit_options':
            case 'delivery_time':
            case 'delivery_times':
            case 'order_types':
            case 'currencies':
            case 'vat_rates':
            case 'customer_segments':
            case 'bank_names':
                return hasPermission('customers', 'create') || hasPermission('sales', 'create') || hasPermission('orders', 'create') || hasPermission('quotations', 'create');
            case 'vendor_group':
            case 'vendor_groups':
            case 'vendor_type':
            case 'vendor_types':
            case 'vendor_categories':
                return hasPermission('vendors', 'create') || hasPermission('purchasing', 'create');
            case 'material_subtypes':
            case 'paper_types':
            case 'coatings':
            case 'wave_types':
            case 'packaging_types':
            case 'brands':
            case 'unit':
            case 'units':
            case 'uom':
                return hasPermission('materials', 'create') || hasPermission('products', 'create') || hasPermission('warehouse', 'create');
            case 'shift':
            case 'shifts':
            case 'defect_reason':
            case 'defect_reasons':
            case 'downtime_reason':
            case 'downtime_reasons':
            case 'priority':
                return hasPermission('production', 'create') || hasPermission('quality', 'create');
            case 'service_type':
            case 'service_types':
            case 'delivery_type':
                return hasPermission('deliveries', 'create') || hasPermission('warehouse', 'create');
            case 'plate_size':
            case 'plate_sizes':
            case 'plate_status':
            case 'plate_statuses':
            case 'ink_color':
            case 'ink_colors':
            case 'tooling_category':
            case 'unit_tooling':
            case 'tooling_status':
            case 'mold_type':
            case 'mold_types':
            case 'mold_status':
            case 'mold_statuses':
            case 'process_stage':
            case 'process_types':
            case 'prepress_types':
            case 'pricing_unit_types':
            case 'process':
            case 'deployment_model':
                return hasPermission('plates', 'create') || hasPermission('molds', 'create') || hasPermission('technical', 'create') || hasPermission('prepress', 'create') || hasPermission('production', 'create');
            case 'ncr_source':
            case 'ncr_sources':
            case 'ncr_severity':
            case 'ncr_severities':
                return hasPermission('quality', 'create');
            case 'machine_type':
            case 'machine_types':
                return hasPermission('machines', 'create') || hasPermission('production', 'create');
            case 'user_position':
            case 'user_section':
            case 'request_types':
                return hasPermission('users', 'create') || hasPermission('hr', 'create');
            default:
                return hasPermission('configs', 'create');
        }
    }, [canAdd, isAdmin, hasPermission, categoryCode]);

    const handleQuickAdd = async (name: string) => {
        const trimmedName = name.trim();
        if (!trimmedName) return null;

        // Prevent duplicate addition locally
        const existing = options.find(
            opt => opt.label.trim().toLowerCase() === trimmedName.toLowerCase() ||
                   opt.id.trim().toLowerCase() === trimmedName.toLowerCase()
        );
        if (existing) {
            toast.warning(`"${trimmedName}" đã có trong danh mục`);
            return existing.id;
        }

        try {
            // Generate clean code fallback
            const generatedCode = trimmedName
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                .toUpperCase()
                .replace(/[^A-Z0-9_-]/g, '_')
                .replace(/_+/g, '_');

            const newItem = await trpcClient.config.addConfigItem.mutate({
                categoryCode: categoryCode,
                name: trimmedName,
                code: generatedCode || trimmedName,
                sortOrder: options.length,
            });

            // Invalidate the query so it re-fetches across all consumers
            await queryClient.invalidateQueries({ queryKey: ['config-lookup'] });
            await queryClient.invalidateQueries({ queryKey: ['trpc', 'config'] });
            toast.success(`Đã thêm "${trimmedName}" vào danh mục`);
            
            // Nếu dùng valueField = 'name' thì trả về name vừa tạo để form điền đúng text
            return valueField === 'name' ? (newItem.name || trimmedName) : (newItem.code || newItem.name);
        } catch (error: any) {
            toast.error(error.message || 'Lỗi khi thêm danh mục');
            return null;
        }
    };

    return (
        <div className={className}>
            <ComboboxConfig
                value={value}
                onChange={onChange}
                options={filteredOptions}
                label={label}
                placeholder={placeholder}
                required={required}
                loading={isLoading}
                error={error}
                disabled={disabled}
                hideLabel={hideLabel}
                quickAddLabel="Thêm mới"
                onQuickAdd={handleQuickAdd}
                canAdd={hasCreatePerm} // Restrict quick add to users with create permission
            />
        </div>
    );
}
